import { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Clock,
  Info,
  Package,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { Preset, PresetGroup } from "../lib/supabase";
import {
  getBuiltInPresets,
  getUserPresets,
  getUserGroups,
  getActiveSchedule,
  createOrUpdateSchedule,
  deleteSchedule,
} from "../lib/api";
import { formatFoodRelation } from "../lib/scheduler";
import { SupplementSelector } from "./SupplementSelector";
import { GapRuleSelector } from "./GapRuleSelector";
import { ConfirmDialog } from "./ConfirmDialog";

interface ScheduleEntryForm {
  preset_id: string;
  group_id: string;
  desired_offset_min: number;
  note: string;
}

interface GapRuleForm {
  source_tag: string | null;
  source_group_id: string | null;
  target_tag: string | null;
  target_group_id: string | null;
  min_gap_min: number;
}

export function ScheduleBuilder() {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [groups, setGroups] = useState<(PresetGroup & { presets: Preset[] })[]>(
    []
  );
  const [entries, setEntries] = useState<ScheduleEntryForm[]>([]);
  const [gapRules, setGapRules] = useState<GapRuleForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showGapRules, setShowGapRules] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newlyAddedIndex, setNewlyAddedIndex] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [builtin, user, userGroups, schedule] = await Promise.all([
        getBuiltInPresets(),
        getUserPresets(),
        getUserGroups(),
        getActiveSchedule(),
      ]);

      setPresets([...builtin, ...user]);
      setGroups(userGroups);

      if (schedule) {
        setHasSchedule(true);
        setEntries(
          schedule.entries.map((e) => ({
            preset_id: e.preset_id || "",
            group_id: e.group_id || "",
            desired_offset_min: e.desired_offset_min,
            note: e.note || "",
          }))
        );
        setGapRules(
          schedule.gap_rules.map((r) => ({
            source_tag: r.source_tag || null,
            source_group_id: r.source_group_id || null,
            target_tag: r.target_tag || null,
            target_group_id: r.target_group_id || null,
            min_gap_min: r.min_gap_min,
          }))
        );
      } else {
        setHasSchedule(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const addEntry = () => {
    const newIndex = entries.length;
    setEntries([
      ...entries,
      { preset_id: "", group_id: "", desired_offset_min: 0, note: "" },
    ]);

    // Show visual feedback for the newly added entry
    setNewlyAddedIndex(newIndex);
    setTimeout(() => setNewlyAddedIndex(null), 2000);
  };

  const updateEntry = (
    index: number,
    field: keyof ScheduleEntryForm,
    value: string | number
  ) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const removeEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const addGapRule = () => {
    setGapRules([
      ...gapRules,
      {
        source_tag: null,
        source_group_id: null,
        target_tag: null,
        target_group_id: null,
        min_gap_min: 60,
      },
    ]);
  };

  const updateGapRule = (
    index: number,
    field: keyof GapRuleForm,
    value: string | number
  ) => {
    const updated = [...gapRules];
    updated[index] = { ...updated[index], [field]: value };
    setGapRules(updated);
  };

  const removeGapRule = (index: number) => {
    setGapRules(gapRules.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const validEntries = entries.filter((e) => e.preset_id || e.group_id);
    if (validEntries.length === 0) {
      alert("Add at least one dose entry");
      return;
    }

    setSaving(true);
    try {
      console.log("Saving schedule with entries:", validEntries);
      console.log("Saving schedule with gap rules:", gapRules);
      await createOrUpdateSchedule(validEntries, gapRules);
      alert("Schedule saved successfully!");
      setHasSchedule(true);
    } catch (error) {
      console.error("Error saving schedule:", error);
      alert(
        `Error saving schedule: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSchedule = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSchedule = async () => {
    setDeleting(true);
    try {
      console.log("Starting schedule deletion...");
      await deleteSchedule();
      console.log("Schedule deletion completed successfully");

      // Clear the local state
      setEntries([]);
      setGapRules([]);
      setHasSchedule(false);

      alert(
        "Schedule and daily plans deleted successfully! You can now create a new schedule from scratch."
      );
    } catch (error) {
      console.error("Error deleting schedule:", error);

      // Provide better error messaging
      let errorMessage = "Unknown error occurred";

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null) {
        errorMessage = JSON.stringify(error);
      } else {
        errorMessage = String(error);
      }

      alert(`Error deleting schedule: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatOffset = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getPresetById = (id: string) => presets.find((p) => p.id === id);
  const getGroupById = (id: string) => groups.find((g) => g.id === id);

  const uniqueTags = [...new Set(presets.map((p) => p.tag))].sort();

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 safe-area-x">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Build Schedule
            </h1>
            <p className="text-gray-600">
              Define your daily supplement schedule relative to wake time
            </p>
          </div>
          {hasSchedule && (
            <button
              onClick={handleDeleteSchedule}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
            >
              <Trash2 size={16} />
              {deleting ? "Deleting..." : "Delete Schedule"}
            </button>
          )}
        </div>
      </div>

      <div className="bg-warm-light border border-warm-solid rounded-lg p-4 mb-6 flex gap-3 warm-shadow">
        <Info className="text-warm-secondary flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-warm-dark">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Set desired time as minutes after you wake up</li>
            <li>Add gap rules to enforce minimum spacing between tags</li>
            <li>Lower priority items shift forward to resolve conflicts</li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Dose Entries</h2>
        </div>

        {entries.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              {hasSchedule
                ? "No doses yet. Add your first dose below."
                : "Start building your daily supplement schedule!"}
            </p>
            {!hasSchedule && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto mb-6">
                <p className="text-sm text-green-800">
                  <span className="font-medium">Fresh start!</span> Create your
                  personalized schedule by adding doses below.
                </p>
              </div>
            )}
            <button
              onClick={addEntry}
              className="flex items-center gap-2 px-6 py-3 bg-warm-primary text-white rounded-lg hover:bg-warm-hover text-sm font-medium mx-auto warm-shadow"
            >
              <Plus size={16} />
              Add First Dose
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry, index) => {
              const preset = getPresetById(entry.preset_id);
              const isNewlyAdded = newlyAddedIndex === index;
              return (
                <div
                  key={index}
                  className={`border border-gray-200 rounded-lg p-4 transition-all duration-500 ${
                    isNewlyAdded
                      ? "border-green-300 bg-green-50 shadow-lg"
                      : "hover:border-gray-300"
                  }`}
                >
                  {isNewlyAdded && (
                    <div className="flex items-center gap-2 mb-3 text-green-700">
                      <CheckCircle size={16} />
                      <span className="text-sm font-medium">
                        New dose added!
                      </span>
                    </div>
                  )}
                  <div className="flex gap-3 items-start">
                    <div className="flex-1 space-y-3">
                      <SupplementSelector
                        presets={presets}
                        groups={groups}
                        selectedPresetId={entry.preset_id}
                        selectedGroupId={entry.group_id}
                        onChange={(presetId, groupId) => {
                          console.log("ScheduleBuilder onChange called:", {
                            index,
                            presetId,
                            groupId,
                          });
                          const updated = [...entries];
                          updated[index] = {
                            ...updated[index],
                            preset_id: presetId,
                            group_id: groupId,
                          };
                          setEntries(updated);
                        }}
                        label="Supplement or Group"
                        placeholder="Select supplement or group..."
                      />

                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            <Clock size={12} className="inline mr-1" />
                            After Wake (minutes)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="5"
                            value={entry.desired_offset_min || ""}
                            placeholder="0"
                            onChange={(e) =>
                              updateEntry(
                                index,
                                "desired_offset_min",
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        {entry.desired_offset_min > 0 && (
                          <div className="flex items-end">
                            <span className="text-sm text-gray-600 pb-2">
                              = {formatOffset(entry.desired_offset_min)} after
                              wake
                            </span>
                          </div>
                        )}
                      </div>

                      <input
                        type="text"
                        placeholder="Optional note..."
                        value={entry.note}
                        onChange={(e) =>
                          updateEntry(index, "note", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />

                      {preset && (
                        <div className="flex gap-2 text-xs">
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {preset.tag}
                          </span>
                          <span className="bg-warm-light text-warm-secondary px-2 py-1 rounded">
                            Priority {preset.priority}
                          </span>
                        </div>
                      )}
                      {entry.group_id && getGroupById(entry.group_id) && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="text-orange-600" size={16} />
                            <span className="text-sm font-medium text-orange-900">
                              {getGroupById(entry.group_id)!.name}
                            </span>
                          </div>
                          <div className="text-xs text-orange-700 space-y-1">
                            {getGroupById(entry.group_id)!.presets.map((p) => (
                              <div key={p.id}>• {p.name}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => removeEntry(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg mt-1"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Add Dose button after the last entry */}
            <div className="pt-2">
              <button
                onClick={addEntry}
                className="flex items-center gap-2 px-4 py-2 bg-warm-primary text-white rounded-lg hover:bg-warm-hover text-sm font-medium warm-shadow"
              >
                <Plus size={16} />
                Add Dose
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Gap Rules</h2>
            <p className="text-sm text-gray-600">
              Enforce minimum spacing between supplement tags
            </p>
          </div>
          <button
            onClick={() => setShowGapRules(!showGapRules)}
            className="text-sm text-warm-secondary hover:text-warm-dark font-medium"
          >
            {showGapRules ? "Hide" : "Show"}
          </button>
        </div>

        {showGapRules && (
          <>
            <button
              onClick={addGapRule}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium mb-4"
            >
              <Plus size={16} />
              Add Rule
            </button>

            {gapRules.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                No gap rules defined.
              </p>
            ) : (
              <div className="space-y-4">
                {gapRules.map((rule, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex gap-3 items-start">
                      <GapRuleSelector
                        tags={uniqueTags}
                        groups={groups}
                        selectedTag={rule.source_tag || ""}
                        selectedGroupId={rule.source_group_id || ""}
                        onChange={(tag, groupId) => {
                          const updated = [...gapRules];
                          updated[index] = {
                            ...updated[index],
                            source_tag: tag,
                            source_group_id: groupId,
                          };
                          setGapRules(updated);
                        }}
                        label="Source"
                        placeholder="Select source tag or group..."
                      />

                      <div className="flex flex-col items-center justify-center pt-8">
                        <span className="text-gray-600 text-sm whitespace-nowrap">
                          must be ≥
                        </span>
                      </div>

                      <div className="flex-shrink-0 pt-6">
                        <input
                          type="number"
                          min="0"
                          step="15"
                          value={rule.min_gap_min}
                          onChange={(e) =>
                            updateGapRule(
                              index,
                              "min_gap_min",
                              parseInt(e.target.value) || 0
                            )
                          }
                          className="w-20 px-3 py-2 text-sm border border-warm rounded-lg focus:ring-2 focus:ring-warm-primary bg-warm-glass"
                        />
                        <div className="text-xs text-gray-500 text-center mt-1">
                          minutes
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-center pt-8">
                        <span className="text-gray-600 text-sm whitespace-nowrap">
                          after
                        </span>
                      </div>

                      <GapRuleSelector
                        tags={uniqueTags}
                        groups={groups}
                        selectedTag={rule.target_tag || ""}
                        selectedGroupId={rule.target_group_id || ""}
                        onChange={(tag, groupId) => {
                          const updated = [...gapRules];
                          updated[index] = {
                            ...updated[index],
                            target_tag: tag,
                            target_group_id: groupId,
                          };
                          setGapRules(updated);
                        }}
                        label="Target"
                        placeholder="Select target tag or group..."
                      />

                      <button
                        onClick={() => removeGapRule(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg mt-6 flex-shrink-0"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-warm-primary text-white py-4 rounded-lg font-semibold hover:bg-warm-hover disabled:opacity-50 warm-shadow"
      >
        {saving ? "Saving..." : "Save Schedule"}
      </button>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteSchedule}
        title="Delete Entire Schedule"
        icon={<AlertTriangle className="text-red-600" size={24} />}
        confirmText="Delete Schedule"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        message={
          <div className="space-y-3">
            <p>Are you sure you want to delete your entire schedule?</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="font-medium text-red-900 mb-2">
                This will permanently remove:
              </p>
              <ul className="text-red-800 text-sm space-y-1">
                <li>
                  • {entries.length} dose{" "}
                  {entries.length === 1 ? "entry" : "entries"}
                </li>
                <li>
                  • {gapRules.length} gap{" "}
                  {gapRules.length === 1 ? "rule" : "rules"}
                </li>
                <li>• All your daily plans and doses</li>
              </ul>
            </div>
            <p className="text-sm text-gray-600">
              This action cannot be undone. You'll be able to create a
              completely new schedule from scratch.
            </p>
          </div>
        }
      />
    </div>
  );
}
